import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  initiateCall,
  answerCall,
  declineCall,
  endCall,
  cancelCall,
  listenForIncomingCalls,
  listenForCallChanges,
  sendOffer,
  sendAnswer,
  sendIceCandidate,
  listenForOffers,
  listenForAnswers,
  listenForIceCandidates,
} from '../services/callService';
import { sendCallLogMessage } from '../services/chatService';

// Import ringtone sound
import ringtoneSound from '../sound effects/ringtone.mp3';

const CallContext = createContext();

// ICE servers for WebRTC
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ],
};

export function CallProvider({ children }) {
  const { user } = useAuth();

  // Call state
  const [currentCall, setCurrentCall] = useState(null);
  const [callStatus, setCallStatus] = useState('idle'); // idle, ringing, calling, connected, ended
  const [incomingCall, setIncomingCall] = useState(null);

  // Media streams
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);

  // WebRTC
  const peerConnectionRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  // Listeners cleanup
  const unsubscribeCallRef = useRef(null);
  const unsubscribeOffersRef = useRef(null);
  const unsubscribeAnswersRef = useRef(null);
  const unsubscribeCandidatesRef = useRef(null);

  // ICE candidate buffer (for candidates that arrive before remote description)
  const iceCandidateBuffer = useRef([]);

  // Guards to prevent duplicate processing
  const hasProcessedOffer = useRef(false);
  const hasProcessedAnswer = useRef(false);
  const isAcceptingCall = useRef(false);

  // Call timing for duration calculation
  const callConnectedAtRef = useRef(null);
  const callStartedAtRef = useRef(null);

  // Flag to prevent duplicate call log messages
  const callLogSentRef = useRef(false);

  // Ringtone audio ref
  const ringtoneRef = useRef(null);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Listen for incoming calls
  useEffect(() => {
    if (!user?.uid) return;

    const unsubscribe = listenForIncomingCalls(user.uid, (call) => {
      // Don't show if already in a call
      if (callStatus !== 'idle') return;
      setIncomingCall(call);
      setCallStatus('ringing');
      // Play ringtone and show notification
      playRingtone();
      showIncomingCallNotification(call);
    });

    return () => unsubscribe();
  }, [user?.uid, callStatus]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupCall();
    };
  }, []);

  // Handle browser close/tab close - CRITICAL for iOS compatibility
  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      if (currentCall?.id && callStatus !== 'idle') {
        console.log('🚪 Browser closing - ending call:', currentCall.id);

        // Use sendBeacon for reliable delivery on page close
        // Fall back to sync fetch if sendBeacon unavailable
        try {
          // First, try to update Firestore directly (may not complete)
          endCall(currentCall.id).catch(() => {});

          // Cleanup local resources immediately
          if (localStream) {
            localStream.getTracks().forEach(track => track.stop());
          }
          if (peerConnectionRef.current) {
            peerConnectionRef.current.close();
          }
        } catch (err) {
          console.error('Error during beforeunload cleanup:', err);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentCall?.id, callStatus, localStream]);

  // Show browser notification for incoming call
  const showIncomingCallNotification = (call) => {
    // Only show if page is not focused
    if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
      const isVideo = call.type === 'video';
      const notification = new Notification(
        `${isVideo ? 'Video' : 'Audio'} Call from ${call.callerName || 'Unknown'}`,
        {
          body: `Incoming ${isVideo ? 'FaceTime' : 'voice'} call`,
          icon: call.callerPhotoUrl || '/icons/call-icon.png',
          tag: 'incoming-call',
          requireInteraction: true,
          vibrate: [200, 100, 200, 100, 200],
        }
      );

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Store to close later
      window.callNotification = notification;
    }

    // Also flash the title
    flashTabTitle(call);
  };

  // Flash browser tab title for incoming call
  const flashTabTitle = (call) => {
    const originalTitle = document.title;
    const isVideo = call.type === 'video';
    const callTitle = `📞 ${isVideo ? 'Video' : 'Audio'} Call - ${call.callerName || 'Incoming'}`;
    let isFlashing = true;

    const flashInterval = setInterval(() => {
      if (!isFlashing) {
        clearInterval(flashInterval);
        document.title = originalTitle;
        return;
      }
      document.title = document.title === originalTitle ? callTitle : originalTitle;
    }, 1000);

    window.callTitleFlash = { interval: flashInterval, originalTitle, stop: () => { isFlashing = false; } };
  };

  // Play ringtone for incoming calls
  const playRingtone = () => {
    try {
      // Stop any existing ringtone
      stopRingtone();

      const audio = new Audio(ringtoneSound);
      audio.loop = true;
      audio.volume = 0.7;
      ringtoneRef.current = audio;

      // Play with user interaction fallback
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.log('Ringtone autoplay blocked, waiting for interaction');
        });
      }
    } catch (e) {
      console.log('Could not play ringtone:', e);
    }
  };

  // Stop ringtone and cleanup notifications
  const stopRingtone = () => {
    // Stop audio
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current = null;
    }

    // Close notification
    if (window.callNotification) {
      window.callNotification.close();
      window.callNotification = null;
    }

    // Stop title flashing
    if (window.callTitleFlash) {
      window.callTitleFlash.stop();
      document.title = window.callTitleFlash.originalTitle;
      window.callTitleFlash = null;
    }
  };

  // Get user media
  const getUserMedia = async (video = true) => {
    try {
      console.log('🎤 Requesting user media - video:', video, 'audio: true');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: video ? { width: 1280, height: 720 } : false,
        audio: true,
      });

      // Debug: Log audio tracks
      console.log('🎤 Local audio tracks:', stream.getAudioTracks().length);
      stream.getAudioTracks().forEach(track => {
        console.log('🎤 Audio track:', track.label,
                    'enabled:', track.enabled,
                    'muted:', track.muted,
                    'readyState:', track.readyState);
      });

      if (video) {
        console.log('📹 Local video tracks:', stream.getVideoTracks().length);
      }

      setLocalStream(stream);
      return stream;
    } catch (error) {
      console.error('❌ Error getting user media:', error);
      throw error;
    }
  };

  // Create peer connection
  const createPeerConnection = useCallback((callId, remoteUserId, isInitiator) => {
    console.log('🔗 Creating peer connection - isInitiator:', isInitiator);
    const pc = new RTCPeerConnection(ICE_SERVERS);
    peerConnectionRef.current = pc;

    // Add local stream tracks
    if (localStream) {
      localStream.getTracks().forEach((track) => {
        console.log('➕ Adding track to peer connection:', track.kind, 'enabled:', track.enabled);
        pc.addTrack(track, localStream);
      });
    }

    // Handle remote stream/track
    pc.ontrack = (event) => {
      console.log('🎵 Track received:', event.track.kind,
                  'streams:', event.streams.length,
                  'enabled:', event.track.enabled,
                  'muted:', event.track.muted,
                  'readyState:', event.track.readyState);

      const [stream] = event.streams;
      setRemoteStream(stream);

      // Debug: Log all tracks in the stream
      if (stream) {
        console.log('📥 Remote stream tracks - audio:', stream.getAudioTracks().length,
                    'video:', stream.getVideoTracks().length);
        stream.getAudioTracks().forEach(track => {
          console.log('📥 Remote audio track:', track.label,
                      'enabled:', track.enabled,
                      'muted:', track.muted);
        });
      }
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('🧊 ICE candidate generated:', event.candidate.candidate?.substring(0, 50) + '...');
        // Wrap in try-catch to prevent permission errors when call ends
        sendIceCandidate(callId, user.uid, remoteUserId, event.candidate).catch(err => {
          // Silently ignore permission errors (call may have ended)
          if (!err.message?.includes('permissions')) {
            console.error('Error sending ICE candidate:', err);
          }
        });
      } else {
        console.log('🧊 ICE candidate gathering complete');
      }
    };

    // Log ICE gathering state changes
    pc.onicegatheringstatechange = () => {
      console.log('🧊 ICE gathering state:', pc.iceGatheringState);
    };

    // Log signaling state changes
    pc.onsignalingstatechange = () => {
      console.log('📡 Signaling state:', pc.signalingState);
    };

    // Handle connection state
    pc.onconnectionstatechange = () => {
      console.log('🔗 Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setCallStatus('connected');
        // Track when call actually connected for duration
        if (!callConnectedAtRef.current) {
          callConnectedAtRef.current = new Date();
          console.log('📞 Call connected at:', callConnectedAtRef.current);
        }

        // Debug: Log senders and receivers when connected
        console.log('🔍 Connection established - checking audio');
        pc.getSenders().forEach(sender => {
          if (sender.track) {
            console.log('📤 Sending track:', sender.track.kind,
                        'enabled:', sender.track.enabled,
                        'muted:', sender.track.muted);
          }
        });
        pc.getReceivers().forEach(receiver => {
          if (receiver.track) {
            console.log('📥 Receiving track:', receiver.track.kind,
                        'enabled:', receiver.track.enabled,
                        'muted:', receiver.track.muted,
                        'readyState:', receiver.track.readyState);
          }
        });
      } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        handleCallEnded();
      }
    };

    // Handle ICE connection state for more reliable disconnect detection
    pc.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'disconnected') {
        // Give it a moment to recover, then end if still disconnected
        setTimeout(() => {
          if (peerConnectionRef.current?.iceConnectionState === 'disconnected' ||
              peerConnectionRef.current?.iceConnectionState === 'failed') {
            console.log('ICE disconnected/failed - ending call');
            handleCallEnded();
          }
        }, 3000);
      } else if (pc.iceConnectionState === 'failed') {
        handleCallEnded();
      }
    };

    return pc;
  }, [localStream, user?.uid]);

  // Start a call
  const startCall = async (calleeId, calleeName, calleePhoto, callType = 'video', chatId = null) => {
    try {
      // Reset guards
      hasProcessedOffer.current = false;
      hasProcessedAnswer.current = false;
      iceCandidateBuffer.current = [];
      callLogSentRef.current = false;
      callStartedAtRef.current = new Date();
      callConnectedAtRef.current = null;

      setCallStatus('calling');

      // Get user media
      const stream = await getUserMedia(callType === 'video');

      // Create call document with chatId for call log messages
      const call = await initiateCall(
        user.uid,
        calleeId,
        user.displayName || user.email,
        user.photoURL,
        callType,
        chatId,
        calleeName
      );
      setCurrentCall({ ...call, calleeName, calleePhoto, chatId });

      // Create peer connection
      const pc = createPeerConnection(call.id, calleeId, true);

      // Add tracks to connection
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
      });

      // Listen for call status changes
      unsubscribeCallRef.current = listenForCallChanges(call.id, (updatedCall) => {
        if (!updatedCall) return;

        if (updatedCall.status === 'answered') {
          // Callee answered - wait briefly to ensure their listeners are ready
          // This prevents race condition where offer arrives before callee's listeners are set up
          console.log('📞 Call answered, waiting for callee listeners to be ready...');
          setTimeout(() => {
            console.log('📞 Sending offer to callee...');
            createAndSendOffer(call.id, calleeId);
          }, 500); // 500ms delay to ensure listeners are ready
        } else if (updatedCall.status === 'declined' || updatedCall.status === 'ended' || updatedCall.status === 'missed') {
          handleCallEnded();
        }
      });

      // Listen for answers (only process first one)
      unsubscribeAnswersRef.current = listenForAnswers(call.id, user.uid, async (fromUserId, answer) => {
        // Guard: Only process first answer
        if (hasProcessedAnswer.current) {
          console.log('Already processed answer, ignoring duplicate');
          return;
        }
        hasProcessedAnswer.current = true;

        console.log('📥 Received answer from:', fromUserId);

        // Debug: Check answer SDP for audio
        console.log('📥 Answer SDP has audio:', answer.sdp?.includes('m=audio'));
        console.log('📥 Answer SDP has OPUS codec:', answer.sdp?.includes('opus'));

        if (peerConnectionRef.current) {
          try {
            await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
            console.log('✅ Remote description set successfully');

            // Flush buffered ICE candidates
            console.log(`Flushing ${iceCandidateBuffer.current.length} buffered ICE candidates`);
            for (const candidate of iceCandidateBuffer.current) {
              try {
                await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
              } catch (err) {
                console.error('Error adding buffered ICE candidate:', err);
              }
            }
            iceCandidateBuffer.current = [];
          } catch (err) {
            console.error('❌ Error handling answer:', err);
            hasProcessedAnswer.current = false; // Allow retry on error
          }
        }
      });

      // Listen for ICE candidates
      unsubscribeCandidatesRef.current = listenForIceCandidates(call.id, user.uid, async (candidate) => {
        console.log('Received ICE candidate');
        if (peerConnectionRef.current) {
          if (peerConnectionRef.current.remoteDescription) {
            try {
              await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
            } catch (err) {
              console.error('Error adding ICE candidate:', err);
            }
          } else {
            // Buffer candidate until remote description is set
            console.log('Buffering ICE candidate (no remote description yet)');
            iceCandidateBuffer.current.push(candidate);
          }
        }
      });

    } catch (error) {
      console.error('Error starting call:', error);
      setCallStatus('idle');
      throw error;
    }
  };

  // Create and send WebRTC offer
  const createAndSendOffer = async (callId, remoteUserId) => {
    if (!peerConnectionRef.current) return;

    console.log('📝 Creating WebRTC offer...');
    const offer = await peerConnectionRef.current.createOffer();

    // Debug: Check SDP for audio
    console.log('📝 Offer SDP has audio:', offer.sdp.includes('m=audio'));
    console.log('📝 Offer SDP has OPUS codec:', offer.sdp.includes('opus'));

    await peerConnectionRef.current.setLocalDescription(offer);
    await sendOffer(callId, user.uid, remoteUserId, offer);
    console.log('📤 Offer sent to:', remoteUserId);
  };

  // Accept incoming call
  const acceptCall = async () => {
    if (!incomingCall) return;

    // Guard: Prevent multiple accepts
    if (isAcceptingCall.current) {
      console.log('Already accepting call, ignoring duplicate');
      return;
    }
    isAcceptingCall.current = true;

    // Reset guards and timing
    hasProcessedOffer.current = false;
    hasProcessedAnswer.current = false;
    iceCandidateBuffer.current = [];
    callLogSentRef.current = false;
    callStartedAtRef.current = incomingCall.createdAt?.toDate?.() || new Date();
    callConnectedAtRef.current = null;

    try {
      stopRingtone();
      setCallStatus('connecting');
      setCurrentCall(incomingCall);

      // Get user media FIRST
      const stream = await getUserMedia(incomingCall.type === 'video');

      // Create peer connection BEFORE answering (so we're ready for offers)
      const pc = createPeerConnection(incomingCall.id, incomingCall.callerId, false);

      // Add tracks
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
      });

      // Set up listeners BEFORE answering (critical for timing!)
      // Listen for offers from caller (only process first one)
      unsubscribeOffersRef.current = listenForOffers(incomingCall.id, user.uid, async (fromUserId, offer) => {
        // Guard: Only process first offer
        if (hasProcessedOffer.current) {
          console.log('Already processed offer, ignoring duplicate');
          return;
        }
        hasProcessedOffer.current = true;

        console.log('📥 Received offer from:', fromUserId);

        // Debug: Check offer SDP for audio
        console.log('📥 Offer SDP has audio:', offer.sdp?.includes('m=audio'));
        console.log('📥 Offer SDP has OPUS codec:', offer.sdp?.includes('opus'));

        if (peerConnectionRef.current) {
          try {
            await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(offer));
            console.log('✅ Remote description set successfully');

            // Flush buffered ICE candidates now that remote description is set
            console.log(`Flushing ${iceCandidateBuffer.current.length} buffered ICE candidates`);
            for (const candidate of iceCandidateBuffer.current) {
              try {
                await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
              } catch (err) {
                console.error('Error adding buffered ICE candidate:', err);
              }
            }
            iceCandidateBuffer.current = [];

            console.log('📝 Creating WebRTC answer...');
            const answer = await peerConnectionRef.current.createAnswer();

            // Debug: Check answer SDP for audio
            console.log('📝 Answer SDP has audio:', answer.sdp.includes('m=audio'));
            console.log('📝 Answer SDP has OPUS codec:', answer.sdp.includes('opus'));

            await peerConnectionRef.current.setLocalDescription(answer);
            await sendAnswer(incomingCall.id, user.uid, fromUserId, answer);
            console.log('📤 Sent answer to:', fromUserId);
          } catch (err) {
            console.error('❌ Error handling offer:', err);
            hasProcessedOffer.current = false; // Allow retry on error
          }
        }
      });

      // Listen for ICE candidates
      unsubscribeCandidatesRef.current = listenForIceCandidates(incomingCall.id, user.uid, async (candidate) => {
        console.log('Received ICE candidate');
        if (peerConnectionRef.current) {
          if (peerConnectionRef.current.remoteDescription) {
            try {
              await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
            } catch (err) {
              console.error('Error adding ICE candidate:', err);
            }
          } else {
            // Buffer candidate until remote description is set
            console.log('Buffering ICE candidate (no remote description yet)');
            iceCandidateBuffer.current.push(candidate);
          }
        }
      });

      // Listen for call status changes
      unsubscribeCallRef.current = listenForCallChanges(incomingCall.id, (updatedCall) => {
        if (!updatedCall) return;
        if (updatedCall.status === 'ended') {
          handleCallEnded();
        }
      });

      // NOW answer the call in Firestore (after listeners are ready)
      await answerCall(incomingCall.id);
      console.log('Call answered, waiting for offer from caller');

      setIncomingCall(null);
    } catch (error) {
      console.error('Error accepting call:', error);
      isAcceptingCall.current = false; // Reset guard on error
      setCallStatus('idle');
      setIncomingCall(null);
    }
  };

  // Reject incoming call
  const rejectCall = async () => {
    if (!incomingCall) return;

    stopRingtone();
    await declineCall(incomingCall.id);

    // Send call log message for declined call
    if (incomingCall.chatId && !callLogSentRef.current) {
      callLogSentRef.current = true;
      try {
        await sendCallLogMessage(incomingCall.chatId, {
          callId: incomingCall.id,
          callerId: incomingCall.callerId,
          callerName: incomingCall.callerName,
          calleeId: user.uid,
          calleeName: user.displayName || user.email,
          callType: incomingCall.type,
          status: 'declined',
          duration: null,
          initiatedAt: incomingCall.createdAt?.toDate?.() || new Date(),
          endedAt: new Date()
        });
        console.log('✅ Call declined log sent');
      } catch (error) {
        console.error('❌ Error sending declined call log:', error);
      }
    }

    setIncomingCall(null);
    setCallStatus('idle');
  };

  // End current call
  const hangUp = async () => {
    if (!currentCall) return;

    if (callStatus === 'calling') {
      await cancelCall(currentCall.id);
    } else {
      await endCall(currentCall.id);
    }

    handleCallEnded();
  };

  // Handle call ended
  const handleCallEnded = async (status = 'ended') => {
    // Send call log message if we have a chatId and haven't sent one yet
    if (currentCall?.chatId && !callLogSentRef.current) {
      callLogSentRef.current = true;

      try {
        const now = new Date();
        const duration = callConnectedAtRef.current
          ? Math.floor((now - callConnectedAtRef.current) / 1000)
          : null;

        console.log('📝 Sending call log - status:', status, 'duration:', duration);

        await sendCallLogMessage(currentCall.chatId, {
          callId: currentCall.id,
          callerId: currentCall.callerId,
          callerName: currentCall.callerName,
          calleeId: currentCall.calleeId,
          calleeName: currentCall.calleeName,
          callType: currentCall.type,
          status: status,
          duration: duration,
          initiatedAt: callStartedAtRef.current,
          endedAt: now
        });

        console.log('✅ Call log message sent');
      } catch (error) {
        console.error('❌ Error sending call log message:', error);
      }
    }

    // Reset timing refs
    callConnectedAtRef.current = null;
    callStartedAtRef.current = null;

    stopRingtone();
    cleanupCall();
    setCallStatus('idle');
    setCurrentCall(null);
    setIncomingCall(null);
  };

  // Cleanup call resources
  const cleanupCall = () => {
    // Stop local stream
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      setLocalStream(null);
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Clear ICE candidate buffer and reset guards
    iceCandidateBuffer.current = [];
    hasProcessedOffer.current = false;
    hasProcessedAnswer.current = false;
    isAcceptingCall.current = false;
    callLogSentRef.current = false;

    setRemoteStream(null);

    // Cleanup listeners
    if (unsubscribeCallRef.current) {
      unsubscribeCallRef.current();
      unsubscribeCallRef.current = null;
    }
    if (unsubscribeOffersRef.current) {
      unsubscribeOffersRef.current();
      unsubscribeOffersRef.current = null;
    }
    if (unsubscribeAnswersRef.current) {
      unsubscribeAnswersRef.current();
      unsubscribeAnswersRef.current = null;
    }
    if (unsubscribeCandidatesRef.current) {
      unsubscribeCandidatesRef.current();
      unsubscribeCandidatesRef.current = null;
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        return !audioTrack.enabled;
      }
    }
    return false;
  };

  // Stop ringing (called when caller cancels before answer)
  const stopRinging = useCallback(() => {
    console.log('📞 stopRinging called - clearing incoming call');
    stopRingtone();
    setIncomingCall(null);
    setCallStatus('idle');
  }, []);

  // Toggle video
  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        return !videoTrack.enabled;
      }
    }
    return false;
  };

  const value = {
    // State
    currentCall,
    callStatus,
    incomingCall,
    localStream,
    remoteStream,

    // Refs for video elements
    localVideoRef,
    remoteVideoRef,

    // Actions
    startCall,
    acceptCall,
    rejectCall,
    hangUp,
    toggleMute,
    toggleVideo,
    stopRinging,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCall() {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
}

export default CallContext;
